/**
 * calb011 (CalibrationCompanyCertRecordDetail) embedded ReactTabulator session helpers.
 * Reuses calibration company-cert state-list filter/sorter remaps; does not broaden calb013.
 */
import {
  CALIBRATION_COMPANY_CERT_KIND,
  decorateCalibrationCertTreeRows,
  mapCalibrationCompanyCertHeaderFiltersToQuery,
  mapCalibrationCompanyCertSorterField,
} from './calibrationCertificateStateSync';
import { cleanSorters, extractSafePaginationParams } from './tabulatorProgressiveLoading';

export const CALB011_MASK_PAGE = 'CalibrationCompanyCertRecordDetail';
export const CALB011_PAGED_URL = '/api/company_certificates/paged';
export const CALB011_PRINT_URL = '/api/company_certificates/export/print';
export const CALB011_XLSX_URL = '/api/company_certificates/export/xlsx';
export const CALB011_SHOW_URL = (id) => `/api/company_certificates/${id}`;

export const CALB011_DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  name: true,
  doc_status: true,
  description: true,
  custom_start_of_validity: true,
  custom_end_of_validity: true,
  cert_description: true,
  protocol: true,
  state: true,
  created_at: true,
  updated_at: true,
});

export const CALB011_MASK_DIALOG_FIELDS = Object.freeze([
  'name',
  'doc_status',
  'description',
  'custom_start_of_validity',
  'custom_end_of_validity',
  'cert_description',
  'protocol',
  'state',
  'created_at',
  'updated_at',
]);

/** Map calb011 Slovak doc_status labels onto BE certificate_status values. */
export function normalizeDocStatusFilterValue(val) {
  const v = String(val ?? '').trim();
  if (v === 'Aktívna' || v === 'Aktívny') return 'Aktívny';
  if (v === 'Deaktivovaná' || v === 'Deaktivovaný') return 'Deaktivovaný';
  return v;
}

/**
 * Header filters → server filter_* params for embedded company-scoped paged tree.
 * `doc_status` is remapped to `certificate_status` (same contract as custom_doc_status / custom_cert_status).
 * UI `cert_description` / `description` follow OTK qlty029 aliases onto BE description / record_description.
 */
export function mapCalb011HeaderFiltersToQuery(headerFilters) {
  const remapped = (Array.isArray(headerFilters) ? headerFilters : []).map((f) => {
    if (!f || f.field == null) return f;
    if (f.field === 'doc_status' || f.field === 'cert_status') {
      return {
        ...f,
        field: 'custom_cert_status',
        value: normalizeDocStatusFilterValue(f.value),
      };
    }
    if (f.field === 'cert_description') {
      return { ...f, field: 'description' };
    }
    if (f.field === 'description') {
      return { ...f, field: 'record_description' };
    }
    return f;
  });
  return mapCalibrationCompanyCertHeaderFiltersToQuery(remapped);
}

export function mapCalb011SortersToQuery(sorters) {
  return cleanSorters(sorters)
    .map((s) => {
      const raw = s?.field || s?.column;
      if (raw === 'doc_status' || raw === 'cert_status') {
        return { field: 'certificate_status', dir: s?.dir === 'desc' ? 'desc' : 'asc' };
      }
      if (raw === 'cert_description') {
        return { field: 'description', dir: s?.dir === 'desc' ? 'desc' : 'asc' };
      }
      if (raw === 'description') {
        return { field: 'record_description', dir: s?.dir === 'desc' ? 'desc' : 'asc' };
      }
      const field = mapCalibrationCompanyCertSorterField(raw);
      if (!field) return null;
      return { field, dir: s?.dir === 'desc' ? 'desc' : 'asc' };
    })
    .filter(Boolean);
}

export function decorateCalb011PagedRows(rawRows) {
  const decorated = decorateCalibrationCertTreeRows(Array.isArray(rawRows) ? rawRows : []);
  return decorated.map((c) => {
    if (!c || typeof c !== 'object') return c;
    const docStatus = c.deactivation_date ? 'Deaktivovaná' : 'Aktívna';
    const children = Array.isArray(c._children)
      ? c._children.map((ch) => (ch && typeof ch === 'object'
        ? {
            ...ch,
            doc_status: docStatus,
            cert_status: c.deactivation_date ? 'Deaktivovaný' : 'Aktívny',
            cert_description: c.description,
            files: Array.isArray(ch.files) ? ch.files : (ch.files == null ? [] : ch.files),
          }
        : ch))
      : [];
    return {
      ...c,
      doc_status: docStatus,
      cert_status: c.deactivation_date ? 'Deaktivovaný' : 'Aktívny',
      cert_description: c.description,
      _children: children,
    };
  });
}

/**
 * Build paged query extras for the embedded calb011 table.
 */
export function buildCalb011RequestParams({
  paramsFromTabulator = {},
  pageSize,
  companyId,
  getTable,
  toolbarSearch = '',
} = {}) {
  const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
  if (extra.size == null) extra.size = pageSize;
  extra.type = 'calibration';
  if (companyId != null && companyId !== '') extra.company_id = companyId;

  const rawSorters = paramsFromTabulator?.sorters ?? getTable?.()?.getSorters?.() ?? [];
  const mapped = mapCalb011SortersToQuery(rawSorters);
  if (mapped.length) extra.sorters = JSON.stringify(mapped);

  const hf = getTable?.()?.getHeaderFilters?.() ?? [];
  Object.assign(extra, mapCalb011HeaderFiltersToQuery(hf));

  const search = String(toolbarSearch ?? '').trim();
  if (search !== '') extra.toolbar_search = search;
  return extra;
}

/** Same-client emit payload for calb010 / calb013 sync bus. */
export function buildCalb011MutationDetail({
  kind,
  companyId,
  certificateId,
  recordId,
  changedFields,
} = {}) {
  const detail = { kind };
  if (companyId != null && companyId !== '') {
    detail.companyId = companyId;
    detail.company_id = companyId;
  }
  if (certificateId != null && certificateId !== '') detail.certificateId = certificateId;
  if (recordId != null && recordId !== '') detail.recordId = recordId;
  if (Array.isArray(changedFields) && changedFields.length) detail.changedFields = changedFields;
  return detail;
}

/** Create / delete / activate / deactivate → invalidate progressive session. */
export function isCalb011SessionInvalidatingKind(kind) {
  return (
    kind === CALIBRATION_COMPANY_CERT_KIND.PARENT_CREATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.PARENT_DELETED
    || kind === CALIBRATION_COMPANY_CERT_KIND.PARENT_ACTIVATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.PARENT_DEACTIVATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.RECORD_CREATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.RECORD_DELETED
  );
}

/**
 * Safe in-place update only when the parent certificate row is already loaded.
 * Missing/unloaded rows must not use addData on a remote progressive session.
 */
export function shouldCalb011ApplySafeParentUpdate(kind, rowLoaded) {
  if (!rowLoaded) return false;
  return (
    kind === CALIBRATION_COMPANY_CERT_KIND.PARENT_UPDATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.RECORD_UPDATED
    || kind === CALIBRATION_COMPANY_CERT_KIND.RECORD_FILE_CHANGED
  );
}
